import React, { useEffect, useState } from "react";
import { Sparkles, X, BookOpen } from "lucide-react";
import { VERSION } from "../../config/version";
import {
  getReleaseNotes,
  WHATS_NEW_STORAGE_KEY,
} from "../../config/releaseNotes";
import {
  ensureWhatsNewGuideNote,
  ensureDeepgramGuideNote,
} from "../../services/workspaceService";
import deskGlass from "../../styles/deskGlass";

const styles = deskGlass;

export default function WhatsNewModal() {
  const notes = getReleaseNotes(VERSION);
  const [open, setOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // Hot-reload / local: never block the desk with a modal on every version bump.
  const isDev = process.env.NODE_ENV === "development";

  useEffect(() => {
    if (isDev || !notes) return;
    try {
      const seen = localStorage.getItem(WHATS_NEW_STORAGE_KEY);
      if (seen !== VERSION) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, [notes, isDev]);

  // Always seed Deepgram reference note (idempotent).
  useEffect(() => {
    if (isDev) return;
    ensureDeepgramGuideNote().catch((e) => {
      console.warn("[WhatsNew] Deepgram guide seed failed:", e);
    });
  }, [isDev]);

  useEffect(() => {
    if (isDev || !open || !notes) return;
    let cancelled = false;
    (async () => {
      setSeeding(true);
      try {
        await ensureWhatsNewGuideNote(VERSION);
      } catch (e) {
        console.warn("[WhatsNew] no se pudo crear la nota guía:", e);
      } finally {
        if (!cancelled) setSeeding(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, notes, isDev]);

  const dismiss = () => {
    try {
      localStorage.setItem(WHATS_NEW_STORAGE_KEY, VERSION);
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  if (isDev || !open || !notes) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 10050,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={() => dismiss()}
    >
      <div
        style={{
          ...styles.glassSection,
          maxWidth: 480,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          margin: 0,
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => dismiss()}
          aria-label="Cerrar"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            background: "none",
            border: "none",
            color: "rgba(255,255,255,0.5)",
            cursor: "pointer",
            padding: 4,
          }}
        >
          <X size={20} />
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Sparkles size={22} color="#D4AF37" />
          <div>
            <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.45)", letterSpacing: "0.08em" }}>
              NOVEDADES · v{VERSION}
            </div>
            <h2 id="whats-new-title" style={{ margin: "4px 0 0", fontSize: "1.25rem", color: "#fff" }}>
              {notes.title}
            </h2>
          </div>
        </div>

        <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "0.95rem", lineHeight: 1.5, marginBottom: 16 }}>
          {notes.summary}
        </p>

        <h3 style={{ fontSize: "0.75rem", color: "#D4AF37", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
          Qué cambió
        </h3>
        <ul style={{ margin: "0 0 16px", paddingLeft: 18, color: "rgba(255,255,255,0.8)", fontSize: "0.9rem", lineHeight: 1.55 }}>
          {notes.highlights.map((h) => (
            <li key={h} style={{ marginBottom: 6 }}>{h}</li>
          ))}
        </ul>

        <h3 style={{ fontSize: "0.75rem", color: "#D4AF37", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>
          Cómo usarlo
        </h3>
        <ol style={{ margin: "0 0 20px", paddingLeft: 18, color: "rgba(255,255,255,0.8)", fontSize: "0.9rem", lineHeight: 1.55 }}>
          {notes.howTo.map((h) => (
            <li key={h} style={{ marginBottom: 6 }}>{h}</li>
          ))}
        </ol>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={dismiss}
            style={{
              ...styles.glassActionBtn,
              width: "100%",
              height: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            <BookOpen size={16} />
            Entendido
          </button>
          <p style={{ margin: 0, fontSize: "0.75rem", color: "rgba(255,255,255,0.4)", textAlign: "center" }}>
            {seeding
              ? "Guardando guía en Ayuda › Novedades…"
              : "La guía quedó pinneada en el vault (buscá «Novedades»)."}
          </p>
        </div>
      </div>
    </div>
  );
}
